import React from 'react';
import {
  useMediaQuery,
  Dialog,
  IconButton,
  Typography,
  Box,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { X as CloseIcon } from 'lucide-mui';
import UpgradeView from './upgradeView';
import avatar1 from './upgradeView/avatars/avatar-1.webp';
import avatar2 from './upgradeView/avatars/avatar-2.webp';
import avatar3 from './upgradeView/avatars/avatar-3.webp';

const calculateUserCount = () => {
  const baseCount = 3000;
  const startDate = new Date('2024-12-01');
  const currentDate = new Date();

  // If we haven't reached the start date yet, return base count
  if (currentDate < startDate) {
    return baseCount;
  }

  // Calculate days difference
  const diffTime = Math.abs(currentDate.getTime() - startDate.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Use a seeded random number generator based on the day
  const dailyIncreases = Array.from({ length: diffDays }, (_, index) => {
    const seed = startDate.getTime() + index * 24 * 60 * 60 * 1000;
    const random = Math.sin(seed) * 10000;
    return Math.floor((random - Math.floor(random)) * 21) + 10; // Random number between 10 and 30
  });

  const totalIncrease = dailyIncreases.reduce((sum, curr) => sum + curr, 0);
  return baseCount + totalIncrease;
};

const UpgradeModal = ({ onClose }: { onClose: () => void }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const userCount = calculateUserCount();

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="upgrade-modal"
    >
      <Box
        sx={{
          fontFamily: 'Inter',
          padding: isSmallScreen ? '5px' : '20px 8px 8px 8px',
          position: 'relative',
        }}
      >
        <IconButton
          aria-label="close"
          data-testid="close-upgrade-modal-button"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 4,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon sx={{ width: 20, height: 20 }} />
        </IconButton>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            textAlign: 'center',
            width: '100%',
            marginTop: { xs: '16px', sm: '0' },
          }}
        >
          Upgrade to a Plan
        </Typography>
        <UpgradeView />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mt: -1,
            mb: 0.5,
          }}
        >
          <AvatarGroup
            max={3}
            sx={{
              '& .MuiAvatar-root': {
                width: 24,
                height: 24,
                fontSize: '0.75rem',
              },
            }}
          >
            <Avatar alt="ScreenApp User" src={avatar1} />
            <Avatar alt="ScreenApp User" src={avatar2} />
            <Avatar alt="ScreenApp User" src={avatar3} />
          </AvatarGroup>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            Join {userCount.toLocaleString()} professionals already using
            ScreenApp Premium
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default UpgradeModal;
